import { useState } from 'react';
import {
  DOSE_FORMS, FORM_ICON, FORM_UNITS, dayFields, dayPatternIncomplete, splitStages, stageDates, unitFor,
  type DayPattern, type DoseForm, type Weekday,
} from '@drbell/core';
import { PhotoField, type PhotoUploader } from './PhotoField.tsx';
import { toast } from './Toast.tsx';
import { NoticeList, owedNotices } from './Notices.tsx';

/**
 * Adding a medicine — shared by the organisation console and the caregiver
 * web app (RX-CARE-14).
 *
 * The flow is the phone's: the name exactly as printed, the doctor's own
 * shorthand, "Read it", a sentence saying what DrBell understood to check
 * against the slip, and only then Save. DrBell reads what the doctor wrote;
 * it never suggests a medicine, a dose or a change.
 *
 * Everything that talks to the server comes in through `transport`, so each
 * app keeps its own client and its own idea of the person's time zone.
 *
 * Parity with the phone (docs/33 B5, C3, C4): what kind of medicine it is and
 * what a dose is counted in, which days (some weekdays, every other day,
 * every few days, days on then a break), and tapering — "1-1-1 x 3d then
 * 1-0-1 x 3d" — each stage read back with its dates. The rules are core's.
 */
export interface ParsedNotation {
  ok: true;
  input: string;
  matched: string;
  draft: {
    type: 'meal_relative' | 'interval' | 'prn';
    anchors?: Array<{ meal: 'breakfast' | 'lunch' | 'dinner' | 'bedtime'; offsetMinutes: number; doseQuantity?: number }>;
    intervalHours?: number;
    dailyQuantity?: number;
  };
  foodRelation: string;
  durationDays?: number;
  /** `medium` means something was assumed that the prescriber did not state. */
  confidence: 'high' | 'medium';
  warnings: string[];
  summary: string;
}

export interface ConsentState {
  documents: Array<{
    id: string;
    version: number;
    title: string;
    requiredBeforeFirstUse: boolean;
    accepted: boolean;
    /** Paragraphs, as the API sends them. */
    body?: string[];
  }>;
}

export interface MedicineTransport {
  /** `unit`: what one dose is counted in, so "5" reads as "5 ml" for a syrup. */
  parse: (input: string, unit?: string) => Promise<ParsedNotation>;
  create: (body: Record<string, unknown>) => Promise<unknown>;
  consent: () => Promise<ConsentState>;
  acceptConsent: (documentId: string, version: number) => Promise<unknown>;
  uploader: (kind: 'strip' | 'prescription') => PhotoUploader;
  /** A course "for N days" in the person's own dates, end inclusive. */
  courseDates: (durationDays: number | undefined) => { startDate: string; endDate: string | null };
}

const status = (e: unknown) => (e as { status?: number } | null)?.status;
const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function AddMedicine({ personName, transport, onClose, onAdded }: {
  personName: string;
  transport: MedicineTransport;
  onClose: () => void;
  onAdded: () => void | Promise<void>;
}) {
  const [name, setName] = useState('');
  const [strength, setStrength] = useState('');
  const [notation, setNotation] = useState('');
  const [parsed, setParsed] = useState<ParsedNotation | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isCritical, setIsCritical] = useState(false);
  const [prescribedBy, setPrescribedBy] = useState('');
  const [strip, setStrip] = useState<{ key: string; url: string } | null>(null);
  const [prescription, setPrescription] = useState<{ key: string; url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consent, setConsent] = useState<ConsentState | null>(null);
  const [form, setForm] = useState<DoseForm>('tablet');
  const [doseUnit, setDoseUnit] = useState<string>('tablet');
  const [days, setDays] = useState<DayPattern>({ kind: 'every_day' });
  /** Tapering: the stages after the first, read the same way. */
  const [later, setLater] = useState<ParsedNotation[]>([]);
  const parts = splitStages(notation.trim());
  const today = transport.courseDates(undefined).startDate;
  const stages = parsed && later.length > 0
    ? stageDates([parsed.durationDays, ...later.map((l) => l.durationDays)], today)
    : null;

  // On demand, not per keystroke: "1-0-" is a half-typed prescription.
  const parse = async () => {
    setParseError(null);
    setParsed(null);
    setLater([]);
    try {
      const read = await Promise.all(parts.map((p) => transport.parse(p, unitFor(form, doseUnit))));
      if (read.length > 1 && !stageDates(read.map((r) => r.durationDays), today)) {
        setParseError('Every step but the last needs how long, like "x 3d", so the next one knows when to begin.');
        return;
      }
      setParsed(read[0]!);
      setLater(read.slice(1));
    } catch (e) {
      setParseError(
        status(e) === 422
          ? `Could not read "${notation}". Try the form the slip uses — 1-0-1, BD, TDS, SOS, Q6H — or write the times.`
          : message(e),
      );
    }
  };

  const save = async () => {
    if (!parsed) return;
    setBusy(true);
    setError(null);
    try {
      const { startDate, endDate } = stages ? stages[0]! : transport.courseDates(parsed.durationDays);
      const toSchedule = (d: ParsedNotation['draft']) =>
        d.type === 'prn'
          ? { type: 'prn' as const }
          : d.type === 'interval'
            ? { type: 'interval' as const, intervalHours: d.intervalHours!, anchorTime: '08:00' }
            : { type: 'meal_relative' as const, anchors: d.anchors! };
      await transport.create({
        name: name.trim(),
        strength: strength.trim() || undefined,
        prescribedBy: prescribedBy.trim() || undefined,
        isCritical,
        stripImageKey: strip?.key,
        prescriptionImageKey: prescription?.key,
        // The parser's draft, sent only after the sentence was read.
        schedule:
          parsed.draft.type === 'prn'
            ? { type: 'prn' as const }
            : parsed.draft.type === 'interval'
              ? { type: 'interval' as const, intervalHours: parsed.draft.intervalHours!, anchorTime: '08:00' }
              : { type: 'meal_relative' as const, anchors: parsed.draft.anchors! },
        foodRelation: parsed.foodRelation,
        startDate,
        endDate,
        sourceNotation: stages ? parts[0] : notation.trim(),
        form,
        doseUnit: unitFor(form, doseUnit),
        ...dayFields(days, startDate),
        ...(stages ? {
          stages: later.map((l, i) => ({
            schedule: toSchedule(l.draft),
            startDate: stages[i + 1]!.startDate,
            endDate: stages[i + 1]!.endDate,
            sourceNotation: parts[i + 1],
            foodRelation: l.foodRelation,
          })),
        } : {}),
      });
      toast.success(`${name.trim()} added for ${personName}`, parsed.summary);
      await onAdded();
    } catch (e) {
      if (status(e) === 403) {
        // The consent gate: the notices this person has not yet been shown.
        try {
          setConsent(await transport.consent());
        } catch {
          setError('Please accept the notices before adding a medicine.');
        }
      } else {
        setError(message(e));
      }
      setBusy(false);
    }
  };

  if (consent) {
    return (
      <ConsentGate
        state={consent}
        transport={transport}
        onClose={onClose}
        onAccepted={() => { setConsent(null); setBusy(false); void save(); }}
      />
    );
  }

  const field = 'mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3';

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="add-medicine-title">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-(--line) bg-(--bg) p-5">
        <h2 id="add-medicine-title" className="text-lg font-semibold">A medicine for {personName}</h2>

        <label className="mt-4 block">
          <span className="text-xs text-(--muted)">Name, exactly as printed on the packet</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Metformin" autoFocus className={field} />
          <span className="mt-1 block text-xs text-(--muted)">
            Whichever script the packet uses. Never translate it — a translated name may not match
            anything the pharmacy stocks.
          </span>
        </label>

        <fieldset className="mt-3">
          <legend className="text-xs text-(--muted)">What kind is it?</legend>
          <div className="mt-1 flex flex-wrap gap-2">
            {DOSE_FORMS.map((f) => (
              <button
                key={f}
                type="button"
                aria-pressed={form === f}
                onClick={() => { setForm(f); setDoseUnit(FORM_UNITS[f][0]!); setParsed(null); setLater([]); }}
                className={`min-h-11 rounded-full border px-3 text-sm capitalize ${form === f ? 'border-(--accent) bg-(--accent-soft)' : 'border-(--line)'}`}
              >
                <span aria-hidden="true">{FORM_ICON[f]} </span>{f}
              </button>
            ))}
          </div>
          {FORM_UNITS[form].length > 1 && (
            <label className="mt-2 flex items-center gap-2 text-sm">
              <span className="text-xs text-(--muted)">One dose is counted in</span>
              <select value={unitFor(form, doseUnit)} onChange={(e) => { setDoseUnit(e.target.value); setParsed(null); setLater([]); }} className="min-h-11 rounded-lg border border-(--line) bg-(--panel) px-2">
                {FORM_UNITS[form].map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </label>
          )}
        </fieldset>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs text-(--muted)">Strength — optional</span>
            <input value={strength} onChange={(e) => setStrength(e.target.value)} placeholder="500mg" className={field} />
          </label>
          <label className="block">
            <span className="text-xs text-(--muted)">Prescribed by — optional</span>
            <input value={prescribedBy} onChange={(e) => setPrescribedBy(e.target.value)} placeholder="Dr Iyer" className={field} />
          </label>
        </div>

        <label className="mt-3 block">
          <span className="text-xs text-(--muted)">How it is written on the slip</span>
          <div className="mt-1 flex gap-2">
            <input
              value={notation}
              onChange={(e) => { setNotation(e.target.value); setParsed(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') void parse(); }}
              placeholder="1-0-1 A/F x 7d"
              className="min-h-11 flex-1 rounded-lg border border-(--line) bg-(--panel) px-3 font-mono"
            />
            <button
              onClick={() => void parse()}
              disabled={notation.trim().length < 2}
              className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent) disabled:opacity-40"
            >
              Read it
            </button>
          </div>
          <span className="mt-1 block text-xs text-(--muted)">1-0-1 · BD · TDS · QDS · HS · SOS · Q6H · A/F · P/C · x 7d · a course that steps down: "1-1-1 x 3d then 1-0-1 x 3d"</span>
        </label>

        {parseError && (
          <p role="alert" className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm">{parseError}</p>
        )}

        {parsed && stages && (
          <div className="mt-3 rounded-lg border border-(--accent)/40 bg-(--accent-soft) p-3">
            <p className="text-xs text-(--muted)">This is what DrBell read, step by step. Check it against the slip.</p>
            {[parsed, ...later].map((p, i) => (
              <p key={i} className="mt-1 text-sm">
                <span className="font-medium">Step {i + 1}:</span> {p.summary}
                {' — '}{stages[i]!.startDate}{stages[i]!.endDate ? ` to ${stages[i]!.endDate}` : ', no end date'}
              </p>
            ))}
          </div>
        )}

        {parsed && !stages && (
          <div className="mt-3 rounded-lg border border-(--accent)/40 bg-(--accent-soft) p-3">
            <p className="text-xs text-(--muted)">This is what DrBell read. Check it against the slip.</p>
            <p className="mt-1 font-medium">{parsed.summary}</p>
            {/* The course length, said separately, because the summary does
                not include it and "x 7d" is the part a slip is easiest to
                misread. The dates are the ones that will be saved. */}
            {parsed.durationDays !== undefined && (() => {
              const { startDate, endDate } = transport.courseDates(parsed.durationDays);
              return (
                <p className="mt-1 text-sm">
                  For {parsed.durationDays} day{parsed.durationDays === 1 ? '' : 's'}, today included
                  {endDate ? ` — ${startDate} to ${endDate}` : ''}.
                </p>
              );
            })()}
            {parsed.confidence === 'medium' && <p className="mt-2 text-xs">Some of this was assumed rather than read.</p>}
            {parsed.warnings.length > 0 && (
              <ul className="mt-1 list-inside list-disc text-xs">
                {parsed.warnings.map((w) => <li key={w}>{w}</li>)}
              </ul>
            )}
          </div>
        )}

        {parsed && parsed.draft.type !== 'prn' && <WhichDays value={days} onChange={setDays} />}

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <PhotoField
            label="Photo of the strip"
            hint="Shown on the reminder itself, so the person can check what is in their hand against what is on the screen."
            value={strip}
            uploader={transport.uploader('strip')}
            onChange={setStrip}
          />
          <PhotoField
            label="Photo of the prescription"
            hint="Kept with their prescriptions. Never shown on a reminder."
            value={prescription}
            uploader={transport.uploader('prescription')}
            onChange={setPrescription}
          />
        </div>

        <label className="mt-3 flex min-h-11 items-start gap-2 text-sm">
          <input type="checkbox" checked={isCritical} onChange={(e) => setIsCritical(e.target.checked)} className="mt-1" />
          <span>
            Critical
            <span className="mt-0.5 block text-xs text-(--muted)">
              A missed dose escalates to the family straight away instead of waiting for the window to
              close. For the ones where a late dose matters — insulin, anticoagulants, anti-rejection.
            </span>
          </span>
        </label>

        {error && (
          <p role="alert" className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">{error}</p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">Cancel</button>
          <button
            disabled={busy || !parsed || name.trim().length < 2 || dayPatternIncomplete(days)}
            title={!parsed ? 'Press "Read it" and check what DrBell understood first' : undefined}
            onClick={() => void save()}
            className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? 'Saving…' : 'Add this medicine'}
          </button>
        </div>
      </div>
    </div>
  );
}

const WEEKDAYS: Array<[Weekday, string]> = [[1, 'Mon'], [2, 'Tue'], [3, 'Wed'], [4, 'Thu'], [5, 'Fri'], [6, 'Sat'], [7, 'Sun']];

/**
 * Which days — docs/33 C3, as the phone offers it: every day, some weekdays,
 * every other day, every few days, or some days on then a break. No calendar
 * month: the engine has none.
 */
function WhichDays({ value, onChange }: { value: DayPattern; onChange: (p: DayPattern) => void }) {
  const num = 'min-h-11 w-16 rounded-lg border border-(--line) bg-(--panel) px-2';
  const set = (kind: DayPattern['kind']) => {
    if (kind === 'every_day') onChange({ kind });
    else if (kind === 'weekdays') onChange({ kind, days: [] });
    else if (kind === 'every_other_day') onChange({ kind });
    else if (kind === 'every_n_days') onChange({ kind, n: 3 });
    else onChange({ kind: 'on_off', on: 21, off: 7 });
  };
  return (
    <fieldset className="mt-3">
      <legend className="text-xs text-(--muted)">Which days?</legend>
      <select value={value.kind} onChange={(e) => set(e.target.value as DayPattern['kind'])} className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3">
        <option value="every_day">Every day</option>
        <option value="weekdays">Only on some days of the week</option>
        <option value="every_other_day">Every other day</option>
        <option value="every_n_days">Every few days</option>
        <option value="on_off">Some days on, then a break</option>
      </select>
      {value.kind === 'weekdays' && (
        <div className="mt-2 flex flex-wrap gap-2">
          {WEEKDAYS.map(([d, label]) => {
            const on = value.days.includes(d);
            return (
              <button
                key={d}
                type="button"
                aria-pressed={on}
                onClick={() => onChange({ kind: 'weekdays', days: on ? value.days.filter((x) => x !== d) : [...value.days, d] })}
                className={`min-h-11 min-w-11 rounded-full border px-3 text-sm ${on ? 'border-(--accent) bg-(--accent-soft)' : 'border-(--line)'}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
      {value.kind === 'every_n_days' && (
        <label className="mt-2 flex items-center gap-2 text-sm">
          Once every
          <input type="number" min={3} max={30} value={value.n} onChange={(e) => onChange({ kind: 'every_n_days', n: Math.min(30, Math.max(3, Number(e.target.value) || 3)) })} className={num} />
          days, starting on the first day
        </label>
      )}
      {value.kind === 'on_off' && (
        <label className="mt-2 flex flex-wrap items-center gap-2 text-sm">
          <input type="number" min={1} max={90} value={value.on} onChange={(e) => onChange({ ...value, on: Math.max(1, Number(e.target.value) || 1) })} className={num} />
          days on, then
          <input type="number" min={1} max={90} value={value.off} onChange={(e) => onChange({ ...value, off: Math.max(1, Number(e.target.value) || 1) })} className={num} />
          days off, again and again
        </label>
      )}
      {value.kind === 'weekdays' && value.days.length === 0 && <p className="mt-1 text-xs text-(--muted)">Tick at least one day.</p>}
    </fieldset>
  );
}

/**
 * The notices, shown only on the way to the first medicine — the first moment
 * health data is stored. Reading a dose is never gated.
 */
function ConsentGate({ state, transport, onClose, onAccepted }: {
  state: ConsentState;
  transport: MedicineTransport;
  onClose: () => void;
  onAccepted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const owed = owedNotices(state.documents);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="first-use-title">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-(--line) bg-(--bg) p-5">
        <h2 id="first-use-title" className="text-lg font-semibold">Before the first medicine</h2>
        <p className="mt-1 text-sm text-(--muted)">
          Adding a medicine is the first moment health data is stored here. These say what DrBell is,
          and what happens to that data.
        </p>
        <div className="mt-4">
          <NoticeList notices={owed} />
        </div>
        {error && (
          <p role="alert" className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">{error}</p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">Not now</button>
          <button
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                // One record per document: agreement only to what was shown.
                for (const d of owed) await transport.acceptConsent(d.id, d.version);
                onAccepted();
              } catch (e) {
                setError(message(e));
                setBusy(false);
              }
            }}
            className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? 'Recording…' : 'I have read these'}
          </button>
        </div>
      </div>
    </div>
  );
}
